/**
 * BilderTab — Cover-Hero + Galerie-Grid + Upload-Dropzone + Lightbox.
 * Sub-Components: CoverCard, ImageTile, GalleryLightbox. Reiner lokaler
 * Viewer + Cover-Picker (Fotos gehen NICHT zu OTAs). Backend-Limit 30 Bilder.
 */
import { useState } from 'react';
import { Card, CardBody, EmptyState, Eyebrow, ImagePickerButton } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { useApartmentDetail } from './ApartmentDetailLayout';
import { CoverCard } from '../bilder/CoverCard';
import { ImageTile } from '../bilder/ImageTile';
import { GalleryLightbox } from '../bilder/GalleryLightbox';
import { ACCEPTED_TYPES, MAX_IMAGES, useBilderGallery } from '../bilder/useBilderGallery';

export function BilderTab() {
  const { propertyId: id } = useApartmentDetail();
  const gallery = useBilderGallery(id);
  const { property, images, isLoading, cover, coverFileId, setCover, removeImage } = gallery;
  const [dragging, setDragging] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const atLimit = images.length >= MAX_IMAGES;

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length > 0 && !gallery.uploadProgress) {
      gallery.handleFiles(e.dataTransfer.files);
    }
  };

  if (!property) return null;

  return (
    <>
      <header className="mb-6">
        <Eyebrow>Apartment</Eyebrow>
        <h1 className="text-ink mt-2 font-serif text-3xl tracking-tight">Bilder</h1>
        <p className="text-ink-soft mt-1.5 text-sm">
          Titelbild und Galerie. Die Dateien liegen in der Akte unter „Fotos · Marketing"; an die
          Buchungsportale gehen sie nicht.
        </p>
      </header>

      <CoverCard
        propertyId={id}
        propertyName={property.name}
        cover={cover}
        onClear={() => setCover.mutate(null)}
        clearing={setCover.isPending}
      />

      <Card>
        <CardBody className="p-6">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <Eyebrow variant="muted">Galerie</Eyebrow>
              <p className="text-ink-soft mt-1 text-xs">
                {images.length} von {MAX_IMAGES} Bildern · JPG, PNG oder WebP · höchstens 10 MB
              </p>
            </div>
            <ImagePickerButton
              label="Bilder hochladen"
              variant="brand"
              accept={ACCEPTED_TYPES}
              progress={gallery.uploadProgress}
              disabled={atLimit}
              onFiles={gallery.handleFiles}
            />
          </div>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            className={cn(
              'rounded-md transition-colors',
              dragging && 'bg-sage/5 ring-sage/50 ring-2',
            )}
          >
            {isLoading ? (
              <p className="text-ink-soft p-8 text-sm" role="status">
                Bilder werden geladen…
              </p>
            ) : images.length === 0 ? (
              <EmptyState
                icon="Plus"
                title={dragging ? 'Hier ablegen' : 'Noch keine Bilder'}
                description='Ziehen Sie Bilder hierher oder nutzen Sie „Bilder hochladen" — auch mehrere auf einmal. Das erste Bild wird automatisch zum Titelbild.'
              />
            ) : (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                {images.map((img, i) => (
                  <ImageTile
                    key={img.id}
                    propertyId={id}
                    image={img}
                    isCover={img.id === coverFileId}
                    onSetCover={() => setCover.mutate(img.id)}
                    onDelete={() => removeImage.mutate(img.id)}
                    onOpen={() => setLightboxIndex(i)}
                  />
                ))}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {lightboxIndex !== null && images[lightboxIndex] && (
        <GalleryLightbox
          propertyId={id}
          images={images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </>
  );
}
